<template>
  <div class="illionStatementUploader">
    <slot name="illion-statement-uploader-header" />

    <spinner v-show="!electronicStatusReceived" />

    <div v-show="electronicStatusReceived">
      <!-- Electronic Connection View (default) -->
      <div v-if="currentView === 'electronic'">
        <div :class="{ 'disabled-content': disabledElectronicMode }">
          <slot name="illion-statement-uploader-online-tab-content" />
          <illion-flow-controller
            :institutionLogos="institutionLogos"
            :application="application"
            :updateApplicationWithStore="updateApplicationWithStore"
            @switchToManualMode="onSwitchToManualMode"
            @electronicUploadSuccessful="onElectronicUploadSuccessful"
            @electronicUploadInProgress="onElectronicUploadInProgress"
            @electronicStatusReceived="onElectronicStatusReceived"
          >
            <slot name="illion-flow-consents" slot="illion-flow-consents" />
          </illion-flow-controller>
        </div>
      </div>

      <!-- Manual Upload View -->
      <div v-if="currentView === 'manual'">
        <div :class="{ 'disabled-content': disableManualMode }">
          <slot name="illion-statement-uploader-manual-tab-content" />
          <manual-verification
            :applicationConsents="application.consents"
            class="manualVerification"
          />
          <brand-button
            v-if="!disabledElectronicMode"
            type="button"
            class="backToElectronicButton"
            @click="onBackToElectronic"
          >
            {{ $t('statementCollect.backToElectronicLabel') }}
          </brand-button>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters } from 'vuex';
import uploadFilesMixin from '@/mixins/upload-files';
import ManualVerification from '@/components/ManualVerification';
import IllionFlowController from '@/components/illionComponents/IllionFlowController';
import { ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR } from '@/modules/illion-adrs';
import adrStatuses from '@/constants/adr-statuses';

const VIEW_MODES = {
  ELECTRONIC: 'electronic',
  MANUAL: 'manual',
};

export default {
  name: 'illion-statement-uploader',
  components: {
    ManualVerification,
    IllionFlowController,
  },
  mixins: [uploadFilesMixin],
  props: {
    application: {
      type: Object,
      required: true,
    },
    updateApplicationWithStore: {
      type: Function,
      required: true,
    },
  },
  data() {
    return {
      currentView: VIEW_MODES.ELECTRONIC,
      electronicUploadSuccessful: false,
      isElectronicUploadInProgress: false,
      electronicStatusReceived: false,
      institutionLogos: [
        { svgName: 'aus_institution_logo_commonwealth', slug: 'cba' },
        { svgName: 'aus_intitution_logo_anz', slug: 'anz' },
        { svgName: 'aus_institution_logo_nab', slug: 'nab' },
        { svgName: 'aus_intitution_logo_westpac', slug: 'westpac' },
        { svgName: 'aus_intitution_logo_stgeorge', slug: 'stgeorge' },
        { svgName: 'aus_institution_logo_bendigo', slug: 'bendigo' },
      ],
    };
  },
  computed: {
    ...mapGetters({
      allFiles: 'file/getAll',
      adrs: 'adrs/getAdrs',
      isUploadInProgress: 'file/isUploadInProgress',
    }),
    uploadedFiles() {
      return this.allFiles.filter(file => file.isUploaded);
    },
    disableManualMode() {
      return (
        this.electronicUploadSuccessful ||
        this.isElectronicUploadInProgress ||
        !!this.application.brokerflowCode
      );
    },
    disabledElectronicMode() {
      return (
        this.someStatementsUploaded || this.isUploadInProgress || !!this.application.brokerflowCode
      );
    },
    someStatementsUploaded() {
      return this.requestedStatementAdrs.some(adr =>
        this.uploadedFiles.some(file => file.adrId === adr.id),
      );
    },
    requestedStatementAdrs() {
      return this.adrs.filter(
        adr =>
          adr.status === adrStatuses.REQUESTED &&
          [ILLION_PRE_SUBMIT_ADR, ILLION_POST_SUBMIT_ADR].includes(adr.template.name),
      );
    },
  },
  mounted() {
    if (this.someStatementsUploaded) {
      this.currentView = VIEW_MODES.MANUAL;
    }
  },
  watch: {
    someStatementsUploaded(newVal) {
      if (newVal) {
        this.currentView = VIEW_MODES.MANUAL;
      }
    },
  },
  methods: {
    onSwitchToManualMode() {
      if (!this.disableManualMode) {
        this.currentView = VIEW_MODES.MANUAL;
      }
    },
    onBackToElectronic() {
      if (!this.disabledElectronicMode) {
        this.currentView = VIEW_MODES.ELECTRONIC;
      }
    },
    onElectronicUploadSuccessful() {
      this.electronicUploadSuccessful = true;
      this.$emit('electronicUploadSuccessful');
    },
    onElectronicUploadInProgress(value) {
      this.isElectronicUploadInProgress = value;
    },
    onElectronicStatusReceived() {
      this.electronicStatusReceived = true;
    },
  },
};
</script>
<style lang="sass" scoped>
.spinner
  margin: baseunit(10) auto baseunit(10)

.disabled-content
  pointer-events: none
  opacity: 0.5
  cursor: not-allowed

.illionStatementUploader
  border: 1px solid $color-system-neutral-gray-3
  border-radius: 4px
  padding: $glass-spacing-3
  margin-bottom: $glass-spacing-6

.backToElectronicButton
  margin-top: $glass-spacing-3
  background-color: $color-white-four
  color: $glass-color-brand-primary-light-06
  border: 1px solid $glass-color-brand-primary-light-06
  box-shadow: 0 0 0 4px $glass-color-system-neutral-white
  &:hover, &.demo-hover
    color: $glass-color-brand-primary-dark-03
    background-color: mix($glass-color-brand-primary-light-06, white, 20%)
    border: 1px solid $glass-color-brand-primary-light-06
</style>
